import { useEffect, useState } from "react";
import { Droplets, FileCheck2, FlaskConical, ShieldCheck, Stethoscope, Thermometer } from "lucide-react";
import { rdCro } from "@/content/site";
import { caseStudyCards } from "@/content/caseStudies";
import { rndForm } from "@/content/forms";
import usePageMeta from "@/hooks/usePageMeta";
import { PageHero } from "@/components/sections/PageHero";
import { ProductSection } from "@/components/sections/ProductSection";
import { IconCard, StepStrip } from "@/components/sections/IconCards";
import { CaseCard } from "@/components/sections/CaseCard";
import { CaseModal } from "@/components/sections/CaseModal";
import { CtaBand } from "@/components/sections/CtaBand";
import { FormSection } from "@/components/forms/FormSection";
import { SectionHeader } from "@/components/common/SectionHeader";
import { ButtonLink } from "@/components/common/ButtonLink";
import { Reveal } from "@/components/motion/Reveal";
import { getLenis } from "@/components/layout/SmoothScroll";

const ICONS = { clinical: Stethoscope, safety: ShieldCheck, formulation: FlaskConical, stability: Thermometer, permeation: Droplets, regulatory: FileCheck2 };
const CASES = caseStudyCards.filter((c) => c.category === "rd-cro");

export default function RdCro() {
  usePageMeta(rdCro.meta);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const lenis = getLenis();
    if (selected) lenis?.stop();
    else lenis?.start();
  }, [selected]);

  return (
    <>
      <PageHero hero={rdCro.hero} testId="rdcro-hero">
        <div className="mt-10">
          <ButtonLink to={rdCro.contactTo} variant="accent" testId="rdcro-hero-cta">
            {rdCro.button}
          </ButtonLink>
        </div>
      </PageHero>

      <ProductSection s={rdCro.why} />

      <section className="section-y border-t border-line" data-testid="rdcro-services">
        <div className="container-x">
          <SectionHeader eyebrow="Services" title="What we do" className="mb-14" />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {rdCro.services.map((s, i) => (
              <IconCard key={s.id} icon={ICONS[s.id]} title={s.title} body={s.body} i={i} testId={`rdcro-service-${s.id}`} />
            ))}
          </div>
        </div>
      </section>

      <section className="section-y border-t border-line" data-testid="rdcro-how">
        <div className="container-x">
          <SectionHeader eyebrow="Process" title="How we work" className="mb-12" />
          <Reveal>
            <StepStrip steps={rdCro.steps} testId="rdcro-steps" />
          </Reveal>
        </div>
      </section>

      <section className="section-y border-t border-line" data-testid="rdcro-case-studies">
        <div className="container-x">
          <SectionHeader eyebrow="Case studies" title="Programmes SPC runs in India" className="mb-12" />
          <div className="grid gap-6 md:grid-cols-2">
            {CASES.map((c) => (
              <CaseCard key={c.id} c={c} onOpen={setSelected} />
            ))}
          </div>
        </div>
      </section>
      <CaseModal c={selected} onClose={() => setSelected(null)} />

      <FormSection id="project" intro={rdCro.formIntro} config={rndForm} idPrefix="rnd" />
      <CtaBand title={rdCro.cta} button={rdCro.button} to={rdCro.contactTo} />
    </>
  );
}
